import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const OvertimeRequestsContext = createContext();

export const OvertimeRequestsProvider = ({ children }) => {
  const [overtimeRequests, setOvertimeRequests] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    include: "user",
  });

  const handleSearchChange = (value) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      search: value,
      currentPage: 1,
    }));
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      handleSearchChange(e.target.value);
    }
  };

  const fetchOvertimeRequests = useCallback(async () => {
    setOvertimeRequests((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/ahr/overtime-requests${queryString}`);
      if (response.success) {
        setOvertimeRequests({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setOvertimeRequests({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchOvertimeRequests();
  }, [fetchOvertimeRequests]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = overtimeRequests.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const Pagination = () => PaginationComponent;

  return (
    <OvertimeRequestsContext.Provider
      value={{
        overtimeRequests,
        filterOptions,
        handleSearch,
        handleSearchChange,
        refetch: fetchOvertimeRequests,
        Pagination,
      }}
    >
      {children}
    </OvertimeRequestsContext.Provider>
  );
};

export const useOvertimeRequests = () => {
  const context = useContext(OvertimeRequestsContext);
  if (!context) {
    throw new Error("useOvertimeRequests must be used within OvertimeRequestsProvider");
  }
  return context;
};
